// Filtros da lista de conversas (PLANO 5.3). Puros e testáveis.

/** Valor de "qualquer responsável" no seletor — e o vazio de `FILTROS_VAZIOS`. */
export const RESPONSAVEL_TODOS = '';
/** Valor especial: conversas que ninguém assumiu. Não é id de gente nenhuma. */
export const RESPONSAVEL_SEM = 'sem';

export const FILTROS_VAZIOS = {
  busca: '',
  /**
   * Os atalhos rápidos são campos INDEPENDENTES, não um enum.
   *
   * Eram um só (`fila: 'todas' | 'nao_lidas' | 'minhas' | 'sem_responsavel'`),
   * e um enum obriga a escolher: "não lidas" OU "minhas", nunca as duas. A
   * pergunta real de quem atende — "o que é meu e ainda não li?" — não tinha
   * resposta. Separados, todos se combinam por E, entre si e com busca,
   * etiqueta e status.
   */
  apenasNaoLidas: false,
  apenasMinhas: false,
  /**
   * '' = qualquer um; 'sem' = ninguém assumiu; qualquer outro valor = o uid.
   *
   * Convive com `apenasMinhas` de propósito: são a mesma dimensão, mas escolher
   * "Fulano" no seletor e deixar "Meus" marcado resulta em lista vazia — o que
   * é a resposta correta para uma pergunta contraditória, e não um defeito.
   */
  responsavel: RESPONSAVEL_TODOS,
  /**
   * 'todas' virou o padrão quando a lista passou a AGRUPAR por estado.
   *
   * Antes era 'ativas', que escondia as resolvidas — e com a seção
   * "Resolvidas" na tela isso a deixaria permanentemente vazia, parecendo
   * defeito. Agora quem esconde é a própria seção, recolhida por padrão: o
   * histórico continua a um clique, em vez de atrás de um seletor.
   *
   * A capacidade de filtrar por status continua aqui, para quem precisar.
   */
  status: 'todas', // ativas | aberta | pendente | resolvida | todas
  etiqueta: '',
};

/**
 * Seções da lista, por estado do atendimento (PLANO 5.3).
 *
 * Resolvidas nasce recolhida: é o arquivo, não a fila do dia. As outras duas
 * são o trabalho de agora e abrem sozinhas.
 */
export const SECOES = [
  // Aguardando vem primeiro: é a fila que exige ação de alguém AGORA. O que já
  // tem dono pode esperar a rolagem; o que não tem, não.
  { id: 'aguardando', label: 'Aguardando', abertaPorPadrao: true },
  { id: 'em_atendimento', label: 'Em atendimento', abertaPorPadrao: true },
  // "Encerradas" na tela, `resolvidas` no dado: o id e o status 'resolvida'
  // gravado no Firestore NÃO mudam — renomear o dado exigiria migrar documento
  // e mexer nas Functions, para trocar só uma palavra que o atendente lê.
  { id: 'resolvidas', label: 'Encerradas', abertaPorPadrao: false },
  /**
   * Grupos por último e FECHADO por padrão: são conversas de laboratório e de
   * equipe interna, não paciente esperando resposta. Deixá-las na fila de
   * "Aguardando" empurraria para baixo quem realmente precisa de atendimento.
   */
  { id: 'grupos', label: 'Grupos', abertaPorPadrao: false },
];

/**
 * `FILAS` foi removida junto com o enum `fila`: era a lista de abas de uma UI
 * que saiu da tela quando a lista passou a agrupar por seção. Os atalhos de
 * hoje são caixas independentes, e cada uma se descreve sozinha.
 */

export const STATUS = [
  // "Ativas" é o padrão: conversa resolvida some da fila do dia, mas continua
  // acessível — o histórico nunca é apagado.
  { id: 'ativas', label: 'Abertas e pendentes' },
  { id: 'aberta', label: 'Abertas' },
  { id: 'pendente', label: 'Pendentes' },
  { id: 'resolvida', label: 'Resolvidas' },
  { id: 'todas', label: 'Todos os status' },
];

export const temFiltroAtivo = (f) =>
  Object.keys(FILTROS_VAZIOS).some((k) => f[k] !== FILTROS_VAZIOS[k]);

export function aplicarFiltros(conversas, filtros, usuarioId) {
  const busca = filtros.busca.trim().toLowerCase();
  const digitos = busca.replace(/\D/g, '');

  return conversas.filter((c) => {
    if (filtros.apenasNaoLidas && !(c.naoLidas > 0)) return false;
    if (filtros.apenasMinhas && c.responsavelId !== usuarioId) return false;

    if (filtros.responsavel === RESPONSAVEL_SEM) {
      if (c.responsavelId) return false;
    } else if (filtros.responsavel && c.responsavelId !== filtros.responsavel) {
      return false;
    }

    if (filtros.status === 'ativas' && c.status === 'resolvida') return false;
    if (['aberta', 'pendente', 'resolvida'].includes(filtros.status) && c.status !== filtros.status) {
      return false;
    }

    if (filtros.etiqueta && !(c.etiquetas ?? []).includes(filtros.etiqueta)) return false;

    if (busca) {
      // Os DOIS nomes entram na busca: quem renomeou o contato procura pelo
      // nome que salvou, mas quem só conhece o do WhatsApp também precisa
      // achar. Buscar em um só deixaria metade dos contatos inalcançáveis.
      const nome = `${c.contatoNomeSalvo ?? ''} ${c.contatoNome ?? ''}`.toLowerCase();
      const ultima = String(c.ultimaMensagem?.texto ?? '').toLowerCase();
      const telefone = String(c.contatoTelefone ?? '').replace(/\D/g, '');
      const casa =
        nome.includes(busca) ||
        ultima.includes(busca) ||
        (digitos.length >= 3 && telefone.includes(digitos));
      if (!casa) return false;
    }

    return true;
  });
}

/**
 * Quantas conversas da lista têm mensagem não lida.
 *
 * Conta CONVERSAS, não mensagens: o número ao lado da seção responde "quantas
 * pessoas estão esperando", que é a pergunta de quem olha a fila. Somar
 * mensagens daria um número maior e menos acionável — cinco mensagens da mesma
 * pessoa continuam sendo um atendimento.
 *
 * Trocar para somar mensagens seria `reduce((n, c) => n + (c.naoLidas ?? 0), 0)`.
 */
export const contarNaoLidas = (conversas) =>
  (conversas ?? []).filter((c) => (c.naoLidas ?? 0) > 0).length;

/**
 * Quantas conversas cada atalho traria — o número ao lado da caixa.
 *
 * Conta sobre o CANAL INTEIRO, não sobre o resultado filtrado: o número existe
 * para responder "vale a pena marcar isto?" ANTES do clique. Contado sobre o
 * resultado, marcar "Não lidas" faria o número virar o tamanho da própria
 * lista, e ele deixaria de informar qualquer coisa.
 *
 * Substituiu o `contarFilas`, que devolvia quatro contagens para as abas que
 * saíram da tela — e que ninguém lia havia tempo.
 */
export function contarAtalhos(conversas, usuarioId) {
  return {
    naoLidas: conversas.filter((c) => c.naoLidas > 0).length,
    minhas: conversas.filter((c) => c.responsavelId === usuarioId).length,
  };
}

/**
 * Reparte a lista nas seções da lista.
 *
 * A ordem das checagens importa: GRUPO ganha de tudo (é categoria, não estado),
 * depois resolvida. Uma conversa que foi
 * atendida e depois resolvida tem responsável, e sem essa precedência
 * apareceria em "Em atendimento" — de volta à fila do dia depois de encerrada.
 */
export function agruparPorEstado(conversas) {
  const grupos = { em_atendimento: [], aguardando: [], resolvidas: [], grupos: [] };

  for (const c of conversas) {
    // GRUPO vem PRIMEIRO, antes de status e responsável: grupo não se assume
    // nem se encerra, então cair em "Aguardando" por não ter responsável seria
    // enfileirá-lo entre pacientes à espera. É uma categoria, não um estado.
    if (c.ehGrupo) grupos.grupos.push(c);
    else if (c.status === 'resolvida') grupos.resolvidas.push(c);
    else if (c.responsavelId) grupos.em_atendimento.push(c);
    else grupos.aguardando.push(c);
  }

  return grupos;
}
